import type { ChangedFacetDetails } from 'src/components/organisms/ListingsControlsOptions/types';
import type { UIFacet } from 'src/components/types/UIFacet';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectHash } from 'src/redux/navigation/selectors';
import { applyFacetOptionChangeToFacets } from 'src/redux/productsQuery/helpers/facetOptionHelpers';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import { adaptStoreToPageURL, appendNonFacetListingParams } from './adaptStoreToPageURL';
import { buildListingsURL } from './buildListingsURL';
import { buildTaxonomyFacetHref } from './buildTaxonomyListingURL';
import { selectListingsBasePath } from './selectListingsBasePath';
import { serializeFilterPath } from './serializeFilterPath';

type StoreFacet = ReduxApplicationState['productsQuery']['facets'][number];
type StoreFacetOption = StoreFacet['options'][number];

type IndexedFacetEntry = {
  facet: StoreFacet;
  optionsByValue: Map<string, StoreFacetOption>;
};

type ResolvedFacetOption = {
  facet: StoreFacet;
  option: StoreFacetOption;
};

export type FacetOptionWithResetHref = Omit<StoreFacetOption, 'href'> & {
  href?: string;
};

export type FacetWithResetHrefs = Omit<Facet, 'options'> & {
  options: FacetOptionWithResetHref[];
};

const buildFacetsIndex = (
  facets: ReduxApplicationState['productsQuery']['facets'],
): Map<string, IndexedFacetEntry> => {
  const facetsIndex = new Map<string, IndexedFacetEntry>();
  facets.forEach((facet) => {
    facetsIndex.set(facet.alias, {
      facet,
      optionsByValue: new Map(facet.options.map((option) => [option.value, option])),
    });
  });
  return facetsIndex;
};

const resolveLinkableFacetOption = (
  facetAlias: string,
  optionValue: string,
  facetType: 'single' | 'multi',
  facetsIndex: Map<string, IndexedFacetEntry>,
): ResolvedFacetOption | undefined => {
  const entry = facetsIndex.get(facetAlias);
  const facet = entry?.facet;
  if (!facet || facet.type !== facetType) {
    return undefined;
  }

  const option = entry.optionsByValue.get(optionValue);
  if (!option) {
    return undefined;
  }

  if (option.count === 0 && !option.active) {
    return undefined;
  }

  return { facet, option };
};

const buildChangedFacetDetails = (
  facetAlias: string,
  facet: StoreFacet,
  option: StoreFacetOption,
  optionValue: string,
): ChangedFacetDetails => ({
  alias: facetAlias,
  key: facet.key,
  title: facet.title,
  options: [
    {
      ...option,
      active: !option.active,
      value: optionValue,
    },
  ],
});

const buildURLFromNextFacets = (
  state: ReduxApplicationState,
  baseQueryParams: URLSearchParams,
  nextFacets: StoreFacet[],
): string => {
  if ((state.productsQuery.taxonomyAttributes ?? []).length > 0) {
    return buildTaxonomyFacetHref(state, nextFacets);
  }

  const filterPath = serializeFilterPath(nextFacets);
  return buildListingsURL(
    selectListingsBasePath(state),
    filterPath,
    baseQueryParams,
    selectHash(state),
  );
};

const deriveFacetOptionHref = (
  state: ReduxApplicationState,
  baseQueryParams: URLSearchParams,
  facetAlias: string,
  optionValue: string,
  facetType: 'single' | 'multi',
  facetsIndex: Map<string, IndexedFacetEntry>,
): string | undefined => {
  const resolved = resolveLinkableFacetOption(facetAlias, optionValue, facetType, facetsIndex);
  if (!resolved) {
    return undefined;
  }

  const changedFacet = buildChangedFacetDetails(
    facetAlias,
    resolved.facet,
    resolved.option,
    optionValue,
  );
  const nextFacets = applyFacetOptionChangeToFacets(state.productsQuery.facets, changedFacet);

  return buildURLFromNextFacets(state, baseQueryParams, nextFacets);
};

export const deriveFacetOptionResetHref = (
  state: ReduxApplicationState,
  baseQueryParams: URLSearchParams,
  facetAlias: string,
  optionValue: string,
): string | undefined => {
  const facetsIndex = buildFacetsIndex(state.productsQuery.facets);
  const resolved =
    resolveLinkableFacetOption(facetAlias, optionValue, 'multi', facetsIndex) ??
    resolveLinkableFacetOption(facetAlias, optionValue, 'single', facetsIndex);

  if (!resolved) {
    return undefined;
  }

  const changedFacet = buildChangedFacetDetails(
    facetAlias,
    resolved.facet,
    resolved.option,
    optionValue,
  );
  const nextFacets = applyFacetOptionChangeToFacets(state.productsQuery.facets, changedFacet);

  return buildURLFromNextFacets(state, baseQueryParams, nextFacets);
};

export const adaptStoreToActiveFacetResetHrefs = (
  state: ReduxApplicationState,
  activeFacets: Facet[],
): FacetWithResetHrefs[] => {
  const baseQueryParams = adaptStoreToPageURL(state, 1);

  return activeFacets.map((facet) => ({
    ...facet,
    options: facet.options.map((option) => ({
      ...option,
      href: option.active
        ? deriveFacetOptionResetHref(state, baseQueryParams, facet.alias, option.value)
        : undefined,
    })),
  }));
};

export const getClearAllFacetsHref = (state: ReduxApplicationState): string => {
  const params = new URLSearchParams();
  appendNonFacetListingParams(params, state, 1);
  return buildListingsURL(selectListingsBasePath(state), '', params, selectHash(state));
};

export const adaptStoreToUIFacetHrefs = (
  state: ReduxApplicationState,
  uiFacets: UIFacet[],
): UIFacet[] => {
  if (uiFacets.length === 0) {
    return uiFacets;
  }

  const baseQueryParams = adaptStoreToPageURL(state, 1);
  const facetsIndex = buildFacetsIndex(state.productsQuery.facets);

  return uiFacets.map((facet) => ({
    ...facet,
    options: facet.options.map((option) => {
      const href = deriveFacetOptionHref(
        state,
        baseQueryParams,
        facet.alias,
        option.value,
        facet.type,
        facetsIndex,
      );
      return { ...option, href };
    }),
  }));
};
